import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar, Copy, RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import { myCalendarAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * Modal that shows the user their personal .ics subscription URL.
 * They paste it into Google Calendar -> Settings -> Add calendar -> From URL.
 * Once subscribed, all future visits/tasks auto-appear in their calendar.
 */
export const CalendarSubscriptionModal = ({ open, onClose }) => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);

  const backendOrigin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const feedUrl = token ? `${backendOrigin}/api/me/calendar.ics?token=${token}` : '';
  // Google requires HTTPS; replace 'https' with 'webcal' is also accepted by Apple Calendar.
  const webcalUrl = feedUrl ? feedUrl.replace(/^https?:\/\//, 'webcal://') : '';
  const googleAddUrl = feedUrl
    ? `https://calendar.google.com/calendar/u/0/r/settings/addbyurl?url=${encodeURIComponent(feedUrl)}`
    : '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await myCalendarAPI.getToken();
      setToken(res.data.token);
    } catch {
      toast.error('Failed to load calendar token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleCopy = (text, label = 'Link') => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} copied to clipboard`))
      .catch(() => toast.error('Copy failed — please copy manually'));
  };

  const handleRotate = async () => {
    if (!window.confirm('Generate a new subscription link? Any existing Google Calendar subscriptions will stop updating until you re-subscribe with the new link.')) return;
    setLoading(true);
    try {
      const res = await myCalendarAPI.rotateToken();
      setToken(res.data.token);
      toast.success('New subscription link generated');
    } catch {
      toast.error('Failed to rotate');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Revoke calendar subscription? Google/Outlook subscribers will stop seeing updates.')) return;
    setLoading(true);
    try {
      await myCalendarAPI.revokeToken();
      setToken('');
      toast.success('Subscription revoked');
    } catch {
      toast.error('Failed to revoke');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="calendar-sub-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Personal Calendar Subscription
          </DialogTitle>
          <DialogDescription>
            Subscribe once to see ALL your upcoming visits &amp; tasks in Google Calendar, Outlook, or Apple Calendar. New assignments auto-appear (refresh ~every 2 hours).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Feed URL */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Subscription URL</Label>
            <div className="flex gap-2">
              <Input
                value={feedUrl}
                readOnly
                className="font-mono text-xs"
                onClick={(e) => e.target.select()}
                data-testid="calendar-feed-url"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCopy(feedUrl, 'Subscription URL')}
                disabled={!feedUrl || loading}
                data-testid="copy-feed-url-btn"
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* One-click Google Calendar add */}
          <div className="rounded-lg border-2 border-blue-200 bg-blue-50/60 p-3 space-y-2">
            <p className="text-sm font-semibold text-blue-900">Quick Add to Google Calendar</p>
            <p className="text-xs text-blue-800">
              Click below to open Google Calendar&apos;s &quot;Add by URL&quot; page with your link pre-filled. Sign in if asked, then click &quot;Add calendar&quot;. Done.
            </p>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => window.open(googleAddUrl, '_blank', 'noopener,noreferrer')}
              disabled={!googleAddUrl}
              data-testid="open-google-calendar-btn"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in Google Calendar
            </Button>
          </div>

          {/* Other clients */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs text-slate-700">
            <p className="font-semibold text-slate-900">Other calendar apps:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Apple Calendar / iPhone:</strong> Open this link then{' '}
                <button
                  type="button"
                  className="text-blue-600 underline hover:text-blue-800"
                  onClick={() => handleCopy(webcalUrl, 'webcal:// link')}
                >
                  copy webcal:// URL
                </button>
                {' '}and paste in Calendar → File → New Calendar Subscription.
              </li>
              <li><strong>Outlook:</strong> Add calendar → &quot;From Internet&quot; → paste the URL.</li>
              <li><strong>Anywhere else:</strong> Look for &quot;Subscribe by URL&quot; / &quot;iCal feed&quot;.</li>
            </ul>
          </div>

          {/* Privacy / management */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t">
            <p className="text-[11px] text-muted-foreground">
              Keep this URL private — anyone with it can view your upcoming events (read-only).
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRotate}
                disabled={loading}
                data-testid="rotate-token-btn"
                title="Generate a new URL and invalidate the old one"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rotate
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleRevoke}
                disabled={loading || !token}
                data-testid="revoke-token-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarSubscriptionModal;
